import React, { useEffect, useState } from 'react';
import { getSettings, updateSettings, uploadFileToServer } from '../services/api';
import { LayoutDashboard, CheckCircle2, Image as ImageIcon, Code, MousePointerClick } from 'lucide-react';

const ManageSettings = () => {
  const [settings, setSettings] = useState({
    showAboutUs: true,
    showNathTemples: true,
    showOtherTemples: true,
    showBlogs: true,
    showAdAbout: true,
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [savedMessage, setSavedMessage] = useState(false);

  useEffect(() => {
    const fetchSettings = async () => {
      const data = await getSettings();
      setSettings(data);
      setLoading(false);
    };
    fetchSettings();
  }, []);

  const handleToggle = (key) => {
    setSettings((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const handleAdToggle = (sectionKey) => {
    setSettings((prev) => ({
      ...prev,
      homepageAdsConfig: {
        ...prev.homepageAdsConfig,
        [sectionKey]: {
          ...(prev.homepageAdsConfig?.[sectionKey] || { enabled: false, type: 'image', content: '', link: '' }),
          enabled: !(prev.homepageAdsConfig?.[sectionKey]?.enabled)
        }
      }
    }));
  };

  const handleAdChange = (sectionKey, field, value) => {
    setSettings((prev) => ({
      ...prev,
      homepageAdsConfig: {
        ...prev.homepageAdsConfig,
        [sectionKey]: {
          ...(prev.homepageAdsConfig?.[sectionKey] || { enabled: false, type: 'image', content: '', link: '' }),
          [field]: value
        }
      }
    }));
  };

  const handleAdImageUpload = async (e, sectionKey) => {
    const file = e.target.files[0];
    if (!file) return;
    const res = await uploadFileToServer(file);
    if (res.success) {
      handleAdChange(sectionKey, 'content', res.url);
    } else {
      alert("Failed to upload image: " + (res.message || 'Unknown error'));
    }
  };

  const handleSave = async () => {
    setSaving(true);
    await updateSettings(settings);
    setSaving(false);
    setSavedMessage(true);
    setTimeout(() => setSavedMessage(false), 3000);
  };

  if (loading) {
    return (
      <div className="animate-pulse space-y-4">
        <div className="h-8 bg-stone-200 rounded w-1/4 mb-8"></div>
        {[1, 2, 3, 4, 5].map((i) => (
          <div key={i} className="h-16 bg-stone-100 rounded-2xl w-full"></div>
        ))}
      </div>
    );
  }

  const sections = [
    { key: 'showAboutUs', label: 'Show About Us Section', desc: 'Toggle the About Us hero section on the homepage.' },
    { key: 'showNathTemples', label: 'Show Nath Temples Section', desc: 'Toggle the 7 main Nath Temples layout on the homepage.' },
    { key: 'showOtherTemples', label: 'Show Other Temples Section', desc: 'Toggle the dynamic generic templates strip on the homepage.' },
    { key: 'showBlogs', label: 'Show Articles/Blogs Section', desc: 'Toggle the 4 latest articles snippet on the homepage.' },
    { key: 'showAdAbout', label: 'Show Ad Banner on About Page', desc: 'Toggle the Medium Rectangle (MREC) Advertisement on the dedicated /about static page.' },
  ];

  const adSections = [
    { key: 'hero', label: 'After Hero Section' },
    { key: 'about', label: 'After About Us Section' },
    { key: 'nathTemples', label: 'After Nath Temples Section' },
    { key: 'otherTemples', label: 'After Other Temples Section' },
    { key: 'blogs', label: 'After Blogs Section' }
  ];

  return (
    <div>
      <div className="flex justify-between items-end mb-8 pt-2">
        <div>
          <h1 className="text-3xl font-extrabold text-stone-900 mb-2">Homepage & Layout Settings</h1>
          <p className="text-stone-500">Toggle website sections and advertisements dynamically.</p>
        </div>
        <button
          onClick={handleSave}
          disabled={saving}
          className="inline-flex items-center px-6 py-2.5 font-bold rounded-xl text-white bg-saffron-600 hover:bg-saffron-700 transition shadow-md shadow-saffron-600/20 disabled:opacity-50"
        >
          {saving ? 'Saving...' : 'Save Settings'}
        </button>
      </div>

      {savedMessage && (
        <div className="mb-6 p-4 bg-green-50 text-green-700 border border-green-200 rounded-xl flex items-center">
          <CheckCircle2 className="w-5 h-5 mr-2" /> Settings updated successfully!
        </div>
      )}

      <div className="bg-white border border-stone-200 rounded-2xl shadow-sm overflow-hidden">
        <div className="p-6 border-b border-stone-200 bg-stone-50 flex items-center">
          <LayoutDashboard className="w-5 h-5 text-saffron-600 mr-2" />
          <h2 className="text-lg font-bold text-stone-800">Website UI Display Options</h2>
        </div>
        
        <div className="divide-y divide-stone-100">
          {sections.map(({ key, label, desc }) => (
            <div key={key} className="p-6 flex items-center justify-between hover:bg-stone-50 transition-colors">
              <div className="pr-6">
                <h3 className="font-bold text-stone-800 text-lg">{label}</h3>
                <p className="text-sm text-stone-500 mt-1">{desc}</p>
              </div>
              <button
                onClick={() => handleToggle(key)}
                className={`relative inline-flex h-7 w-14 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-saffron-500 focus:ring-offset-2 ${
                  settings[key] ? 'bg-saffron-600' : 'bg-stone-300'
                }`}
              >
                <span
                  className={`inline-block h-5 w-5 transform rounded-full bg-white transition-transform ${
                    settings[key] ? 'translate-x-8' : 'translate-x-1'
                  }`}
                />
              </button>
            </div>
          ))}
        </div>
      </div>

      <div className="bg-white border border-stone-200 rounded-2xl shadow-sm overflow-hidden mt-10">
        <div className="p-6 border-b border-stone-200 bg-stone-50 flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div className="flex items-center">
            <MousePointerClick className="w-5 h-5 text-saffron-600 mr-2" />
            <h2 className="text-lg font-bold text-stone-800">Homepage In-Content Ads Settings</h2>
          </div>
          <p className="text-sm text-stone-500">Configure Adsense scripts or Image Banners seamlessly directly after each section block on the frontend.</p>
        </div>
        
        <div className="divide-y divide-stone-100">
          {adSections.map(({ key, label }) => {
            const config = settings.homepageAdsConfig?.[key] || { enabled: false, type: 'image', content: '', link: '' };
            return (
              <div key={`ad-${key}`} className={`p-6 transition-colors ${config.enabled ? 'bg-saffron-50/30' : 'bg-white'}`}>
                <div className="flex items-center justify-between mb-4">
                  <h3 className="font-bold text-stone-800 text-lg flex items-center">
                    {label}
                  </h3>
                  <button
                    onClick={() => handleAdToggle(key)}
                    className={`relative inline-flex h-7 w-14 items-center rounded-full transition-colors focus:outline-none ${
                       config.enabled ? 'bg-saffron-600' : 'bg-stone-300'
                    }`}
                  >
                    <span className={`inline-block h-5 w-5 transform rounded-full bg-white transition-transform ${config.enabled ? 'translate-x-8' : 'translate-x-1'}`} />
                  </button>
                </div>
                
                {config.enabled && (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-6 bg-stone-50 p-6 rounded-xl border border-stone-200 shadow-inner">
                    <div className="md:col-span-2">
                       <label className="block text-sm font-semibold text-stone-700 mb-2">Advertisement Format</label>
                       <div className="flex space-x-4">
                         <label className="flex items-center cursor-pointer">
                           <input type="radio" value="image" checked={config.type === 'image'} onChange={(e) => handleAdChange(key, 'type', e.target.value)} className="w-4 h-4 text-saffron-600 border-stone-300 focus:ring-saffron-500" />
                           <span className="ml-2 text-stone-800 flex items-center"><ImageIcon className="w-4 h-4 mr-1 text-blue-500"/> Image Banner URL</span>
                         </label>
                         <label className="flex items-center cursor-pointer">
                           <input type="radio" value="adsense" checked={config.type === 'adsense'} onChange={(e) => handleAdChange(key, 'type', e.target.value)} className="w-4 h-4 text-saffron-600 border-stone-300 focus:ring-saffron-500" />
                           <span className="ml-2 text-stone-800 flex items-center"><Code className="w-4 h-4 mr-1 text-green-500"/> Google AdSense Block</span>
                         </label>
                       </div>
                    </div>

                    {config.type === 'image' && (
                      <>
                        <div className="md:col-span-2">
                          <label className="block text-sm font-semibold text-stone-700 mb-1">Banner Image Source</label>
                          <div className="flex flex-col sm:flex-row gap-3">
                            <input type="text" value={config.content} onChange={(e) => handleAdChange(key, 'content', e.target.value)} placeholder="https://..." className="flex-1 border border-stone-300 rounded-lg p-2.5 bg-white focus:ring-2 focus:ring-saffron-500" />
                            <div className="flex items-center whitespace-nowrap">
                              <span className="text-sm font-bold text-stone-400 mx-2">OR</span>
                              <label className="flex items-center cursor-pointer bg-stone-200 hover:bg-stone-300 text-stone-800 px-4 py-2.5 rounded-lg border border-stone-300 transition-colors font-medium">
                                <ImageIcon className="w-4 h-4 mr-2" /> Upload
                                <input type="file" className="hidden" accept="image/*" onChange={(e) => handleAdImageUpload(e, key)} />
                              </label>
                            </div>
                          </div>
                          {config.content && <img src={config.content} alt="Preview" className="mt-4 rounded-lg border border-stone-200 h-24 object-contain bg-white" />}
                        </div>
                        <div className="md:col-span-2">
                          <label className="block text-sm font-semibold text-stone-700 mb-1">Target Link URL (Optional)</label>
                          <input type="url" value={config.link} onChange={(e) => handleAdChange(key, 'link', e.target.value)} placeholder="Where to redirect on click..." className="w-full border border-stone-300 rounded-lg p-2.5 bg-white focus:ring-2 focus:ring-saffron-500" />
                        </div>
                      </>
                    )}

                    {config.type === 'adsense' && (
                      <div className="md:col-span-2">
                        <label className="block text-sm font-semibold text-stone-700 mb-1">AdSense Code Snippet</label>
                        <textarea 
                          value={config.content} 
                          onChange={(e) => handleAdChange(key, 'content', e.target.value)} 
                          placeholder={"<script async src=\"...\"></script>\n<ins class=\"adsbygoogle\" ...></ins>\n<script>\n     (adsbygoogle = window.adsbygoogle || []).push({});\n</script>"}
                          className="w-full border border-stone-300 rounded-lg p-3 bg-stone-800 text-stone-100 font-mono text-sm h-48 focus:ring-2 focus:ring-saffron-500 resize-none"
                        />
                        <p className="text-xs text-stone-500 mt-2">Paste the entire block starting with &lt;script&gt; provided by Google AdSense.</p>
                      </div>
                    )}

                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default ManageSettings;
